import { FC, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import {
  Stack,
  Button,
  Spinner,
  Switch,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import SearchInput from "components/SearchInput";
import { FaFile } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { IHouse, IHouseFormPayload } from "interfaces/house";
import useHouse from "hooks/useHouse";
import ActionsButton from "components/ActionsButton";
import { useDeleteDialog } from "hooks";
import ConfirmDialog from "./ConfirmDialog";
import TextInput from "./TextInput";
import useHouseType from "hooks/useHouseType";
import Select from "./Select";
import { houseValidation } from "utils/validationSchema";
import { useNavigate } from "react-router-dom";

const HouseTable: FC = () => {
  const [filter, setFilter] = useState("");
  const {
    data,
    loading,
    selectedId,
    isLoadingAction,
    updateStatusHouse,
    deleteHouse,
    isOpenForm,
    onCloseForm,
    onSubmitForm,
    formValue,
    openAddForm,
    openEditForm,
  } = useHouse();
  const { data: houseTypeData } = useHouseType();
  const { isOpenDelete, deleteId, onOpendelete, onCloseDelete } =
    useDeleteDialog();

  const houseType = useMemo(() => {
    if (houseTypeData) {
      return houseTypeData.map((item) => ({
        label: item.nama,
        value: item.nama,
      }));
    }
    return [];
  }, [houseTypeData]);

  const columnHelper = createColumnHelper<IHouse>();
  const navigate = useNavigate();

  const columns = [
    columnHelper.accessor("blok", {
      cell: (info) => info.getValue(),
      header: "Blok",
    }),
    columnHelper.accessor("houseType", {
      cell: (info) => info.getValue(),
      header: "Tipe",
      enableGlobalFilter: true,
      enableColumnFilter: true,
    }),
    columnHelper.accessor("devices", {
      cell: (info) =>
        info.getValue() ? (
          info.getValue().length
        ) : (
          <Icon as={MdClose} boxSize={30} color="red" />
        ),
      header: "Perangkat Terinstal",
      enableGlobalFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor("status", {
      cell: (info) => (
        <>
          {info.row.getValue("id") === selectedId ? (
            <Spinner />
          ) : (
            <Switch
              onChange={async (e) => {
                await updateStatusHouse(
                  info.row.getValue("id"),
                  e.target.checked
                );
              }}
              defaultChecked={info.getValue()}
            />
          )}
        </>
      ),
      header: "Status",
      enableGlobalFilter: false,
    }),
    columnHelper.accessor("id", {
      header: () => null,
      cell: (info) => (
        <ActionsButton
          id={info.getValue()}
          actions={[
            {
              label: "Edit",
              onClick: () => openEditForm(info.getValue()),
            },
            { label: "Hapus", onClick: () => onOpendelete(info.getValue()) },
            {
              label: "Perangkat",
              onClick: () => navigate(`/houses/${info.getValue()}/devices`),
            },
          ]}
        />
      ),
      meta: {
        sx: {
          textAlign: "right",
        },
      },
      enableGlobalFilter: false,
      enableSorting: false,
    }),
  ];

  return (
    <>
      <Stack direction="row" alignItems="center" my={6}>
        <SearchInput
          onSearch={(search) => {
            setFilter(search);
          }}
        />
        <Button onClick={openAddForm} leftIcon={<FaFile />}>
          Tambah
        </Button>
      </Stack>
      {data && (
        <DataTable
          isLoading={loading}
          columns={columns}
          data={data}
          filter={filter}
          defaultSort={{
            id: "blok",
            desc: false,
          }}
        />
      )}
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpenForm}
        onClose={onCloseForm}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {formValue.id === "new" ? "Tambah" : "Ubah"} Data
          </ModalHeader>
          <ModalCloseButton />
          <Formik<IHouseFormPayload>
            onSubmit={onSubmitForm}
            initialValues={formValue.initialValues}
            enableReinitialize
            validationSchema={houseValidation}
          >
            <Form>
              <ModalBody>
                <Stack spacing={2}>
                  <TextInput name="blok" id="blok" label="Blok" />
                  <Select
                    label="Tipe Rumah"
                    name="houseType"
                    id="houseType"
                    data={houseType}
                    placeholder="---"
                  />
                </Stack>
              </ModalBody>
              <ModalFooter>
                <Button
                  type="reset"
                  colorScheme="red"
                  mr={3}
                  onClick={onCloseForm}
                >
                  Cancel
                </Button>
                <Button
                  isLoading={isLoadingAction}
                  type="submit"
                  colorScheme="blue"
                >
                  Submit
                </Button>
              </ModalFooter>
            </Form>
          </Formik>
        </ModalContent>
      </Modal>
      <ConfirmDialog
        loading={isLoadingAction}
        isOpen={isOpenDelete}
        onClose={onCloseDelete}
        onConfirm={async () => {
          await deleteHouse(deleteId);
        }}
      />
    </>
  );
};

export default HouseTable;
